import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";

import {
  compassLightGreen,
  compassYellow,
  compassBordeaux,
  greyText,
} from "../../../../styles";
import { SideMenu } from "../../../../components/common/SideMenu";
import {
  BackgroundContainer,
  Card,
  MainColumn as Column,
  NavBarContainer,
  RowContainer,
} from "../../../../components/containers/styledComponents";
import { CompassParagraph } from "../../../../components/common/styledComponents";
import { Avatar } from "../../../../components/visuals/Avatar";
import aiLogo from "../../../../assets/svg/ai_logo.svg";
import { AssessmentTriggerModalV2 } from "../../../../components/common/AIModals";
import { CompassFloatButton } from "../../../../components/buttons/CompassFloatButton";

import { ROUTES } from "../../../../routes/routeNames";
import { useGetEngagementAssessmentV2 } from "../../../../hooks/useGetEngagementAssessmentV2";
import { usePost } from "../../../../hooks/usePost";
import {
  EngagementRecord,
  StewardshipFilterParam,
} from "../../../../models/stewardshipModels";
import {
  CompanySummary,
  CompanySummaryRequest,
} from "../../../../models/CompanyprofileModels";

import { AssessmentPageHeader } from "./AssessmentPageHeader";
import { buildViewModel } from "./assessmentViewModel";
import { AssessmentRunBar } from "./AssessmentRunBar";
import { PillarRadarCard } from "./PillarRadarCard";
import { PillarTelemetryPanel } from "./PillarTelemetryPanel";
import { RedFlagsPanel } from "./RedFlagsPanel";
import { ExpectationsPanel } from "./ExpectationsPanel";
import { AssessmentEmptyState } from "./AssessmentEmptyState";

export const AssessmentViewV2 = (): JSX.Element => {
  const [showSidebar, setShowSidebar] = useState<boolean>(false);
  const { engagementId } = useParams<{ engagementId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const stateEngagement = (
    location.state as { engagementRecord?: EngagementRecord } | null
  )?.engagementRecord;
  const [engagement, setEngagement] = useState<EngagementRecord | null>(
    stateEngagement || null,
  );

  const {
    data: engagementData,
    sendData: getEngagementData,
    isLoading: isEngagementLoading,
  } = usePost<EngagementRecord[], StewardshipFilterParam>();
  const { data: companySummaryData, sendData: getCompanySummary } = usePost<
    CompanySummary,
    CompanySummaryRequest
  >();

  useEffect(() => {
    if (!engagement && engagementId) {
      getEngagementData("GET_ENGAGEMENT_DATA", {
        engagement_record_id: [engagementId],
      });
    }
  }, [engagementId, engagement, getEngagementData]);

  useEffect(() => {
    if (engagementData?.length && engagementId) {
      const found = engagementData.find(
        (e) => e.engagement_record_id === engagementId,
      );
      if (found) setEngagement(found);
    }
  }, [engagementData, engagementId]);

  useEffect(() => {
    if (engagement?.company) {
      getCompanySummary("GET_COMPANY_SUMMARY", {
        compass_cid: engagement.company,
        company_name: "",
      });
    }
  }, [engagement, getCompanySummary]);

  const {
    runs,
    isLoading: isAssessmentLoading,
    error: assessmentError,
    fetchHistory,
  } = useGetEngagementAssessmentV2();

  useEffect(() => {
    if (engagementId) fetchHistory(engagementId);
  }, [engagementId, fetchHistory]);

  const refetchHistory = useCallback((): void => {
    if (engagementId) fetchHistory(engagementId);
  }, [engagementId, fetchHistory]);

  const [selectedAssessedAt, setSelectedAssessedAt] = useState<string | null>(
    null,
  );

  useEffect(() => {
    if (runs.length === 0) {
      if (selectedAssessedAt !== null) setSelectedAssessedAt(null);
      return;
    }
    const stillPresent =
      selectedAssessedAt !== null &&
      runs.some((r) => r.assessed_at === selectedAssessedAt);
    if (!stillPresent) setSelectedAssessedAt(runs[0].assessed_at);
  }, [runs, selectedAssessedAt]);

  const selectedRun = useMemo(() => {
    if (selectedAssessedAt === null) return null;
    return runs.find((r) => r.assessed_at === selectedAssessedAt) ?? null;
  }, [runs, selectedAssessedAt]);

  const view = useMemo(
    () => (selectedRun ? buildViewModel(selectedRun) : null),
    [selectedRun],
  );

  const [showRerunModal, setShowRerunModal] = useState<boolean>(false);
  const openRerunModal = useCallback((): void => setShowRerunModal(true), []);
  const closeRerunModal = useCallback((): void => setShowRerunModal(false), []);
  const handleRerunComplete = useCallback((): void => {
    refetchHistory();
  }, [refetchHistory]);

  const isLoading = isEngagementLoading || isAssessmentLoading;
  const hasRuns = runs.length > 0;
  const showEmptyState =
    !isLoading && !assessmentError && !hasRuns && !!engagementId;

  const radarTint = view
    ? view.healthIndex >= 75
      ? compassLightGreen
      : view.healthIndex >= 50
        ? compassYellow
        : compassBordeaux
    : compassLightGreen;

  return (
    <BackgroundContainer>
      <Column flex={showSidebar ? "0 0 15%" : "0 0 0%"} show={showSidebar}>
        <SideMenu show={showSidebar} setShow={setShowSidebar} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <AssessmentPageHeader
            engagement={engagement}
            engagementId={engagementId}
            companyName={companySummaryData?.name || engagement?.company || ""}
            companyLogoUrl={companySummaryData?.image || ""}
            metaStrip={
              hasRuns ? (
                <AssessmentRunBar
                  runs={runs}
                  view={view}
                  selectedAssessedAt={selectedAssessedAt}
                  onSelect={setSelectedAssessedAt}
                />
              ) : undefined
            }
            onBack={(): void => {
              if (engagementId) {
                navigate(`${ROUTES.SINGLE_ENGAGEMENT}/${engagementId}`, {
                  state: { engagementRecord: engagement },
                });
              }
            }}
          />
        </NavBarContainer>

        {!isLoading && assessmentError && (
          <RowContainer mt={20} jc="center">
            <Card w={60} h="auto" p="40px">
              <CompassParagraph fontSize={16} color={greyText} ta="center">
                {assessmentError}
              </CompassParagraph>
            </Card>
          </RowContainer>
        )}

        {showEmptyState && (
          <AssessmentEmptyState
            engagementRecordId={engagementId as string}
            onSingleComplete={(): void => refetchHistory()}
          />
        )}

        {(isLoading || hasRuns) && (
          <>
            <RowContainer>
              <RedFlagsPanel
                flagged={view?.flaggedSins ?? []}
                clean={view?.sinRows.filter((s) => !s.isFlagged) ?? []}
                isLoading={isLoading}
              />
            </RowContainer>

            <RowContainer jc="flex-start" ai="stretch">
              <PillarRadarCard
                data={view?.radarData ?? []}
                highest={view?.peakPillar?.label ?? "-"}
                lowest={view?.weakestPillar?.label ?? "-"}
                radarColor={radarTint}
                w={40}
                m="0 10px 0 20px"
                isLoading={isLoading}
              />
              <PillarTelemetryPanel
                rows={view?.pillarRows ?? []}
                m="0 20px 0 10px"
                isLoading={isLoading}
              />
            </RowContainer>

            {view && view.expectations.length > 0 && (
              <RowContainer>
                <ExpectationsPanel expectations={view.expectations} />
              </RowContainer>
            )}

            <RowContainer />
          </>
        )}

        {engagementId && (
          <CompassFloatButton
            variant="ai"
            icon={
              <Avatar username="Compass AI" image={aiLogo} size="small" />
            }
            label="Re-run assessment"
            onClick={openRerunModal}
          />
        )}

        {engagementId && (
          <AssessmentTriggerModalV2
            show={showRerunModal}
            onClose={closeRerunModal}
            mode="single"
            engagementRecordId={engagementId}
            onSingleComplete={handleRerunComplete}
          />
        )}
      </Column>
    </BackgroundContainer>
  );
};
